import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUp, Check, ChevronDown, Loader2, Square, X } from 'lucide-react';

/**
 * The building blocks of the Image and Video nodes' composer: a mode switch, input tiles,
 * setting chips with pop-up menus, and the send group (price, balance, round send button).
 *
 * They are put together from the design system's parts (docs/DESIGN_SYSTEM.md) and bring no
 * look of their own: the switch is `ns-seg`, a chip is `ns-chip ns-chip-btn`, its menu is
 * `ns-menu`, the round buttons are `ns-icon-btn`. An accent is the tone of the node the
 * composer is in (`--tone-*`, set on the card), so the same parts are lavender in the Image
 * node and flame in the Video node. Each part gives the icon it is handed its size, so icons
 * match whatever size a node passes.
 *
 * `SettingChip` is also what every other node uses for a setting that picks one of a list
 * (in place of a native select): see its comment for the props.
 */

// ── Mode switch ──────────────────────────────────────────────────────────────

export interface ModeOption<T extends string> {
    value: T;
    label: string;
    icon?: React.ReactNode;
}

export function ModeSwitch<T extends string>({ options, value, onChange, disabled = false, label = 'Mode' }: {
    options: ModeOption<T>[];
    value: T;
    onChange: (value: T) => void;
    disabled?: boolean;
    label?: string;
}) {
    return (
        <div role="tablist" aria-label={label} className="no-drag ns-seg w-fit max-w-full overflow-x-auto no-scrollbar" data-mode-switch>
            {options.map(option => {
                const active = option.value === value;
                return (
                    <button
                        key={option.value}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => onChange(option.value)}
                        disabled={disabled}
                        className="ns-seg-item shrink-0 [&>svg]:w-3.5 [&>svg]:h-3.5"
                    >
                        {option.icon}
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}

// ── Input tiles ──────────────────────────────────────────────────────────────

/**
 * The round button on the corner of a tile that takes what is in it off again. It sits on the
 * corner of its nearest `relative` parent (the tile), and shows while the pointer is over the
 * `group/tile` around it (the tile, or the tile with its tag); it turns red under the pointer,
 * like the button that deletes a node. Its tooltip says what it removes.
 */
export const TileRemoveButton: React.FC<{
    /** What it does, for screen readers: "Remove Start frame". */
    label: string;
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
    disabled?: boolean;
}> = ({ label, onClick, disabled = false }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="node-reveal no-drag ns-icon-btn ns-icon-btn-xs ns-icon-btn-line ns-icon-btn-danger absolute -top-1.5 -right-1.5 shadow-hair opacity-0 group-hover/tile:opacity-100 focus-visible:opacity-100 disabled:opacity-0 group-hover/tile:disabled:opacity-50"
        aria-label={label}
        title={label}
    >
        <X />
    </button>
);

/**
 * A slot for an input: a start frame, image references, clips, audio. Empty, it shows what it
 * takes; filled, it shows what is in it. A click opens the list to choose from. A slot the
 * mode needs and that is still empty is marked in the node's tone.
 */
export const InputTile: React.FC<{
    icon: React.ReactNode;
    label: string;
    /** What fills it: a picture, or a count. */
    filled?: React.ReactNode;
    count?: number;
    onClick?: (anchor: HTMLElement) => void;
    onRemove?: () => void;
    disabled?: boolean;
    title?: string;
    /** Marks a slot the chosen mode needs and that is still empty. */
    needed?: boolean;
}> = ({ icon, label, filled, count = 0, onClick, onRemove, disabled = false, title, needed = false }) => (
    <div className="relative shrink-0 group/tile">
        <button
            type="button"
            onClick={(e) => onClick?.(e.currentTarget)}
            disabled={disabled || !onClick}
            title={title ?? label}
            className={`no-drag relative w-[76px] h-[60px] rounded-lg overflow-hidden flex flex-col items-center justify-center gap-1 text-2xs font-semibold transition-colors disabled:cursor-default ${
                filled ? 'bg-ink text-cream'
                    : needed ? 'bg-[color:var(--tone-soft)] text-[color:var(--tone-deep)] ring-1 ring-inset ring-[color:var(--tone-line)] enabled:hover:bg-[color:var(--tone-line)]'
                        : 'bg-cream-200 text-ink-soft enabled:hover:bg-cream-300 enabled:hover:text-ink'
            }`}
            data-input-tile={label}
        >
            {filled ? (
                <>
                    <span className="absolute inset-0">{filled}</span>
                    <span className="absolute inset-x-0 bottom-0 px-1.5 pt-2 pb-0.5 leading-tight text-left text-white bg-gradient-to-t from-ink/80 to-transparent truncate">{label}</span>
                </>
            ) : (
                <>
                    <span className="[&>svg]:w-4 [&>svg]:h-4">{icon}</span>
                    <span className="leading-none">{label}</span>
                </>
            )}
            {count > 0 && !filled && (
                <span className="ns-badge absolute top-1 right-1 bg-white text-[color:var(--tone-deep)] tabular-nums">{count}</span>
            )}
        </button>
        {onRemove && filled && <TileRemoveButton label={`Remove ${label}`} onClick={onRemove} disabled={disabled} />}
    </div>
);

// ── Setting chips ────────────────────────────────────────────────────────────

/** One thing to choose in a chip's list. `{ value, label }` is all it needs. */
export interface ChipOption<T extends string = string> {
    value: T;
    label: string;
    /** Options under a heading (the model list's providers). */
    group?: string;
    disabled?: boolean;
    /** A few quiet words at the right end of the row. */
    hint?: string;
    /** An icon in front of the label in the list. */
    icon?: React.ReactNode;
}

const MENU_MARGIN = 8;

export function ChipMenu<T extends string>({ anchor, label, options, value, onPick, onClose }: {
    anchor: HTMLElement;
    label: string;
    options: readonly ChipOption<T>[];
    value: T;
    onPick: (value: T) => void;
    onClose: () => void;
}) {
    const panelRef = useRef<HTMLDivElement>(null);
    const [place, setPlace] = useState<{ left: number; top?: number; bottom?: number; maxHeight: number; minWidth: number } | null>(null);
    const enabled = options.filter(option => !option.disabled);
    const [focus, setFocus] = useState(() => Math.max(0, enabled.findIndex(option => option.value === value)));
    const closeRef = useRef(onClose);
    closeRef.current = onClose;

    useLayoutEffect(() => {
        const box = anchor.getBoundingClientRect();
        const below = window.innerHeight - box.bottom - MENU_MARGIN;
        const above = box.top - MENU_MARGIN;
        const openUp = below < 220 && above > below;
        const left = Math.min(Math.max(MENU_MARGIN, box.left), window.innerWidth - 240 - MENU_MARGIN);
        setPlace(openUp
            ? { left, bottom: window.innerHeight - box.top + 4, maxHeight: Math.min(360, above - 4), minWidth: Math.max(160, box.width) }
            : { left, top: box.bottom + 4, maxHeight: Math.min(360, below - 4), minWidth: Math.max(160, box.width) });
    }, [anchor]);
    // A list wider than was reckoned with is moved back inside the window
    useLayoutEffect(() => {
        const panel = panelRef.current;
        if (!place || !panel) return;
        const over = panel.getBoundingClientRect().right - (window.innerWidth - MENU_MARGIN);
        if (over > 0.5 && place.left > MENU_MARGIN) setPlace({ ...place, left: Math.max(MENU_MARGIN, place.left - over) });
    }, [place]);

    useEffect(() => {
        const outside = (e: Event) => {
            const target = e.target as Node;
            if (panelRef.current?.contains(target) || anchor.contains(target)) return;
            closeRef.current();
        };
        const onResize = () => closeRef.current();
        document.addEventListener('pointerdown', outside, true);
        window.addEventListener('wheel', outside, { capture: true, passive: true });
        window.addEventListener('resize', onResize);
        return () => {
            document.removeEventListener('pointerdown', outside, true);
            window.removeEventListener('wheel', outside, true);
            window.removeEventListener('resize', onResize);
        };
    }, [anchor]);

    // Keyboard: arrows move, Enter picks, Esc closes; the chip gets the focus back
    useEffect(() => { panelRef.current?.focus({ preventScroll: true }); }, [place !== null]);
    const onKeyDown = (e: React.KeyboardEvent) => {
        e.stopPropagation(); // not the canvas's shortcuts
        if (e.key === 'Escape') { e.preventDefault(); onClose(); anchor.focus(); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); if (enabled.length) setFocus(i => (i + 1) % enabled.length); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); if (enabled.length) setFocus(i => (i - 1 + enabled.length) % enabled.length); }
        else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); const chosen = enabled[focus]; if (chosen) { onPick(chosen.value); anchor.focus(); } }
    };
    useEffect(() => {
        const el = panelRef.current?.querySelector<HTMLElement>(`[data-option-index="${focus}"]`);
        el?.scrollIntoView?.({ block: 'nearest' });
    }, [focus]);

    if (!place) return null;
    let lastGroup: string | undefined;
    return createPortal(
        <div
            ref={panelRef}
            tabIndex={-1}
            role="listbox"
            aria-label={label}
            onKeyDown={onKeyDown}
            onWheel={(e) => e.stopPropagation()}
            className="ns-menu fixed z-[130] overflow-y-auto outline-none"
            style={{ left: place.left, top: place.top, bottom: place.bottom, maxHeight: place.maxHeight, minWidth: place.minWidth, maxWidth: 280 }}
            data-chip-menu={label}
        >
            {options.map(option => {
                const heading = option.group && option.group !== lastGroup ? option.group : null;
                lastGroup = option.group;
                const index = enabled.indexOf(option);
                const selected = option.value === value;
                return (
                    <React.Fragment key={`${option.group ?? ''}:${option.value}`}>
                        {heading && <div className="ns-menu-label">{heading}</div>}
                        <button
                            type="button"
                            role="option"
                            aria-selected={selected}
                            disabled={option.disabled}
                            onClick={() => { onPick(option.value); anchor.focus(); }}
                            onPointerEnter={() => { if (index >= 0) setFocus(index); }}
                            data-option-index={index >= 0 ? index : undefined}
                            data-active={index >= 0 && index === focus ? 'true' : undefined}
                            title={option.label}
                            className="ns-menu-item"
                        >
                            <span className="w-3.5 shrink-0">{selected && <Check size={14} strokeWidth={2.5} />}</span>
                            {option.icon && <span className="shrink-0 [&>svg]:w-3.5 [&>svg]:h-3.5">{option.icon}</span>}
                            <span className="flex-1 min-w-0 truncate">{option.label}</span>
                            {option.hint && <span className="ns-t-meta shrink-0">{option.hint}</span>}
                        </button>
                    </React.Fragment>
                );
            })}
        </div>,
        document.body,
    );
}

/**
 * The classes of a chip that is pressed, for everything in a node that opens a list or
 * switches something: the design system's `ns-chip ns-chip-btn`. The element tells its state
 * and the class shows it: `aria-expanded="true"` while its list is open, `aria-pressed="true"`
 * for a switch that is on (it is then in the node's tone), `disabled`. `open` and `pressed`
 * give the same look to an element that cannot carry those attributes.
 *
 * In a composer's settings row (`data-settings-row`) a chip keeps 2px clear around itself, so
 * chips in a row that wraps do not touch; anywhere else it has no margin and the row's own
 * gap spaces it. With `shrink` a chip in a tight row narrows instead of pushing the row wider.
 */
export const chipButtonClass = ({ open = false, pressed = false, shrink = false }: { open?: boolean; pressed?: boolean; shrink?: boolean } = {}): string =>
    `no-drag ns-chip ns-chip-btn [[data-settings-row]>&]:m-0.5 ${shrink ? 'min-w-0' : 'shrink-0'} ${
        pressed ? 'ns-chip-tone enabled:hover:bg-[color:var(--tone-line)] enabled:hover:text-[color:var(--tone-deep)]'
            : open ? 'bg-cream-300 text-ink'
                : ''
    }`.trim();

/**
 * One setting as a compact chip: an icon (if any) and its value.
 *
 * With `options` it opens a list to pick one from, in place of a native select:
 *
 *     <SettingChip label="Model" value={id} options={models.map(m => ({ value: m.id, label: m.label }))} onChange={setId} />
 *
 * Without `options` it is a switch (`pressed` says whether it is on; `onChange` is called on a
 * click). The chip shows the chosen option's label, or `display`; the full value is in its
 * tooltip, so a value cut at 150px can still be read.
 */
export function SettingChip<T extends string = string>({ icon, label, value, display, options, onChange, disabled = false, pressed, size, tone, shrink = false, title, className = '' }: {
    /** In front of the value; a chip can do without. */
    icon?: React.ReactNode;
    /** What the setting is, for screen readers and the tooltip: "Model", "Ratio". */
    label: string;
    value?: T;
    /** What the chip shows; the chosen option's label by default. */
    display?: React.ReactNode;
    /** A plain list of `{ value, label }`; `group`, `hint`, `icon` and `disabled` are extras. */
    options?: readonly ChipOption<T>[];
    onChange?: (value: T) => void;
    disabled?: boolean;
    /** For a switch: whether it is on. */
    pressed?: boolean;
    /** `sm` for a chip among small things (a tag on a tile): 11px text, less padding. */
    size?: 'sm';
    /** A tone of its own (`coral`, `teal`, `sage`...): the chip is that tone's soft tint with its deep text, as a tag is. */
    tone?: string;
    /** In a tight row: the chip narrows and its value is cut with an ellipsis rather than the row growing. */
    shrink?: boolean;
    /** The tooltip; "Label: value" by default. */
    title?: string;
    /** More classes for the chip itself (a width, a margin). */
    className?: string;
}) {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const isMenu = !!options;
    const chosen = options?.find(option => option.value === value);
    const shown = display ?? chosen?.label ?? value;
    const full = chosen?.label ?? (typeof shown === 'string' ? shown : '');
    return (
        <>
            <button
                type="button"
                onClick={(e) => {
                    if (!isMenu) { onChange?.(value as T); return; }
                    setAnchor(anchor ? null : e.currentTarget);
                }}
                disabled={disabled}
                aria-label={`${label}: ${typeof shown === 'string' ? shown : ''}`.trim()}
                aria-haspopup={isMenu ? 'listbox' : undefined}
                aria-expanded={isMenu ? !!anchor : undefined}
                aria-pressed={isMenu ? undefined : pressed}
                title={title ?? (isMenu && full ? `${label}: ${full}` : label)}
                data-tone={tone}
                className={`${chipButtonClass({ pressed: !isMenu && !!pressed, shrink })} ${size === 'sm' ? 'ns-chip-sm' : ''} ${
                    tone ? 'ns-chip-tone enabled:hover:bg-[color:var(--tone-line)] enabled:hover:text-[color:var(--tone-deep)] aria-expanded:bg-[color:var(--tone-line)] aria-expanded:text-[color:var(--tone-deep)]' : ''
                } ${className}`.replace(/\s+/g, ' ').trim()}
                data-setting-chip={label}
            >
                {icon && <span className="shrink-0 [&>svg]:w-3.5 [&>svg]:h-3.5">{icon}</span>}
                <span className="truncate min-w-0 max-w-[150px]">{shown}</span>
                {isMenu && <ChevronDown size={12} className={`shrink-0 opacity-60 transition-transform ${anchor ? 'rotate-180' : ''}`} aria-hidden="true" />}
            </button>
            {anchor && options && (
                <ChipMenu
                    anchor={anchor}
                    label={label}
                    options={options}
                    value={value as T}
                    onPick={(next) => { setAnchor(null); if (next !== value) onChange?.(next); }}
                    onClose={() => setAnchor(null)}
                />
            )}
        </>
    );
}

// ── Prompt and settings row ──────────────────────────────────────────────────

/**
 * The prompt field of a composer: the design system's field without a frame, since the card
 * around it is one. It fills the room the node leaves it (its parent is a flex column). (It
 * has no grip to size it by: no textarea in a node card has, the card's own corner sizes it.)
 */
export const promptFieldClass = 'ns-field ns-field-bare flex-1 min-h-0 p-0 border-0';

/**
 * The row under the prompt that holds the setting chips and, at its right end, the send group.
 * With the 2px a chip keeps around itself, the chips are 6px apart and line up with the content
 * above them (12px from the card's edge); in a narrow node they wrap onto a second line.
 */
export const SettingsRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="shrink-0 flex flex-wrap items-center gap-0.5 px-2.5 pt-1.5 pb-2 border-t border-cream-200" data-settings-row>
        {children}
    </div>
);

// ── Send group ───────────────────────────────────────────────────────────────

/**
 * The price and the balance in a soft pill, joined to the round send button. The button is
 * ink, the colour of the main action in every node. While a job runs it spins, or with
 * `onStop` it becomes a stop button.
 */
export const SendGroup: React.FC<{
    price?: React.ReactNode;
    onSend: () => void;
    disabled?: boolean;
    busy?: boolean;
    /** Shown instead of the spinner while busy, for jobs that can be stopped. */
    onStop?: () => void;
    label?: string;
}> = ({ price, onSend, disabled = false, busy = false, onStop, label = 'Generate' }) => (
    <div className={`ns-chip my-0.5 mr-0.5 ml-auto shrink-0 gap-2 py-1 pr-1 ${price ? 'pl-3' : 'pl-1'}`} data-send-group>
        {price && <span className="whitespace-nowrap tabular-nums">{price}</span>}
        {busy && onStop ? (
            <button type="button" onClick={onStop} className="no-drag ns-icon-btn ns-icon-btn-solid" aria-label="Stop" title="Stop">
                <Square fill="currentColor" />
            </button>
        ) : (
            <button
                type="button"
                onClick={onSend}
                disabled={disabled || busy}
                className={`no-drag ns-icon-btn ns-icon-btn-solid ${busy ? 'disabled:opacity-100 disabled:cursor-progress' : ''}`}
                aria-label={busy ? `${label} (running)` : label}
                title={label}
                data-send
            >
                {busy ? <Loader2 className="animate-spin" /> : <ArrowUp strokeWidth={2.5} />}
            </button>
        )}
    </div>
);
